// PMA001_ALIGNMENT_STATE — pure display state for the "Kontrol et ve eşitle" panel.
// Machine identifiers pass through unchanged; tone/labels are decided here so the
// React panel stays presentational and the logic stays testable.

// Same connection tone rules as providerCenterState: these are file-local
// mirrors, never a second authority.
const CONNECTION_ERROR = ['ERROR', 'DENIED', 'AUTH_FAILED', 'INVALID_CREDENTIAL', 'UNAVAILABLE'];
const CONNECTION_READY = ['CONFIGURED', 'READY', 'DISCONNECTED', 'DEGRADED', 'RATE_LIMITED'];

function connectionTone(status) {
  if (status === 'CONNECTED') return 'connected';
  if (CONNECTION_ERROR.includes(status)) return 'error';
  if (CONNECTION_READY.includes(status)) return 'ready';
  return 'off';
}

function vaultTone(item) {
  if (item?.configured !== true) return 'error';
  if (item?.last_verified_status === 'OK') return 'connected';
  return 'ready';
}

export function providerRows(alignment) {
  const providers = Array.isArray(alignment?.providers) ? alignment.providers : [];
  const vaultByKey = new Map(
    (Array.isArray(alignment?.vault) ? alignment.vault : []).map((row) => [row?.provider_key, row]),
  );
  return providers.map((provider) => ({
    provider_key: provider?.provider_key ?? '',
    display_name: provider?.display_name,
    connection_status: provider?.connection_status ?? 'UNKNOWN',
    tone: connectionTone(provider?.connection_status),
    vault: vaultByKey.get(provider?.provider_key) ?? null,
    vault_tone: vaultTone(vaultByKey.get(provider?.provider_key)),
  }));
}

export function catalogSyncRows(alignment) {
  return (Array.isArray(alignment?.catalog_sync) ? alignment.catalog_sync : []).map((row) => ({
    provider_key: row?.provider_key ?? '',
    state: row?.state ?? 'UNKNOWN',
    tone: row?.state === 'SUCCESS' ? 'connected' : row?.state === 'RUNNING' ? 'ready' : row?.state === 'SKIPPED' ? 'ready' : 'error',
    error_code: row?.error_code ?? null,
    models_discovered: row?.models_discovered ?? null,
  }));
}

export function countsRow(alignment) {
  const counts = alignment?.catalog_and_gateway_counts ?? {};
  return {
    catalog_active: counts.catalog_active ?? null,
    gateway_total: counts.gateway_total ?? null,
    gateway_enabled: counts.gateway_enabled ?? null,
  };
}

// Safe alignment proposals: ONLY exact upstream matches taken from the
// ACTIVE catalog. A missing catalog row for a gateway model is review
// material and never proposes a rename, disable or delete.
export function proposals(alignment) {
  const items = Array.isArray(alignment?.matching?.catalog_unmatched)
    ? alignment.matching.catalog_unmatched : [];
  const grouped = new Map();
  for (const item of items) {
    const key = item?.provider_key ?? '';
    if (!grouped.has(key)) grouped.set(key, []);
    // Exact upstream identity only; fuzzy or name-similar candidates are
    // intentionally excluded by the backend query.
    grouped.get(key).push({
      provider_key: key,
      upstream_model: item?.upstream_model ?? '',
      cost_class: item?.cost_class ?? 'UNKNOWN',
    });
  }
  return [...grouped.entries()].map(([provider_key, upstreamModels]) => ({
    provider_key,
    capacity: upstreamModels.length,
    truncated: upstreamModels.length >= 50,
    upstream_models: upstreamModels,
  }));
}

export function gatewayUnmatchedRows(alignment) {
  const rows = Array.isArray(alignment?.matching?.gateway_unmatched)
    ? alignment.matching.gateway_unmatched : [];
  return rows.map((row) => ({
    provider_key: row?.provider_key ?? '',
    model_key: row?.model_key ?? '',
    upstream_model: row?.upstream_model ?? '',
    enabled: row?.enabled === true,
    reason_code: row?.reason_code ?? 'CATALOG_UPSTREAM_NOT_ACTIVE',
  }));
}

export function commercialRow(alignment) {
  const commercial = alignment?.commercial ?? {};
  const counts = commercial.evidence_counts ?? {};
  const unknown = {
    count: commercial.unknown_no_evidence_total ?? null,
    models: Array.isArray(commercial.unknown_no_evidence) ? commercial.unknown_no_evidence : [],
  };
  const free = (counts.PM_ZERO_PRICE_VERIFIED ?? 0) + (counts.PROVIDER_FREE_OVERRIDE ?? 0);
  return {
    free,
    paid: counts.PM_PAID_VERIFIED ?? 0,
    economic_rows: counts.VERIFIED_ROUTE_ECONOMICS ?? 0,
    provider_override: counts.PROVIDER_FREE_OVERRIDE ?? 0,
    missing: counts.MISSING_EVIDENCE ?? 0,
    collision: counts.MODEL_KEY_COLLISION ?? 0,
    unknown,
    tone: unknown.count ? 'ready' : 'connected',
  };
}

export function routesRow(alignment) {
  const routes = alignment?.gateway_routes ?? {};
  const status = routes.status ?? null;
  const tone = status === 'READY_REDUNDANT' || status === 'READY_LIMITED' ? 'connected'
    : status === 'DEGRADED' ? 'ready' : status === 'BLOCKED' ? 'error' : 'off';
  return {
    status,
    tone,
    selectable_route_count: routes.selectable_route_count ?? null,
    selectable_provider_count: routes.selectable_provider_count ?? null,
    excluded_route_count: routes.excluded_route_count ?? null,
  };
}

export function harnessRow(alignment) {
  const harness = alignment?.harness_access ?? {};
  const decision = harness.decision ?? 'UNKNOWN';
  const allowed = harness.allowed === true ? true : harness.allowed === false ? false : null;
  return {
    allowed,
    decision,
    tone: allowed === true ? 'connected' : allowed === null ? 'off' : 'error',
  };
}

export function inferenceRow(alignment) {
  const last = alignment?.last_operator_test ?? null;
  if (!last) return { present: false, tone: 'off', status: null, provider_key: null, model_key: null, occurred_at: null };
  const status = last.status ?? 'UNKNOWN';
  const tone = status === 'PASS' ? 'connected' : status === 'FAIL' ? 'error' : 'ready';
  return { present: true, tone, status, provider_key: last.provider_key, model_key: last.model_key, occurred_at: last.occurred_at };
}

export function lastErrorRows(alignment) {
  return (Array.isArray(alignment?.last_errors) ? alignment.last_errors : []).map((row) => ({
    provider_key: row?.provider_key ?? '',
    model_key: row?.model_key ?? '',
    error_code: row?.error_code ?? 'UNKNOWN',
    circuit_state: row?.circuit_state ?? 'UNKNOWN',
    last_failure_at: row?.last_failure_at ?? null,
  }));
}
